import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Mic,
    Workflow,
    Calendar,
    Eye,
    TrendingUp,
    UserPlus,
    LineChart,
    Film,
    Layers,
    Share2,
    Camera,
    Image as ImageIcon,
    LayoutGrid,
    CheckCircle2,
    ShieldCheck,
    Trophy,
    Target,
    BarChart3,
    Sparkles,
    Hash,
    Megaphone,
    Zap,
    ArrowRight
} from 'lucide-react';

/* ───────────── CONFIG ───────────── */
const SHOW_NAME = "The Show";
const HOST_NAME = "Abir";
/* ────────────────────────────────── */

/* ───────────── Motion primitives ───────────── */
const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

const MotionSection = ({ children, className = "", id }) => (
    <motion.section
        id={id}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-60px" }}
        variants={fadeUp}
        className={className}
    >
        {children}
    </motion.section>
);

/* ───────────── Reusable primitives ───────────── */
const SectionTag = ({ icon: IconComp, label, color = "indigo" }) => {
    const colorClasses = {
        indigo: "bg-indigo-500/10 border-indigo-500/20 text-indigo-300",
        rose: "bg-rose-500/10 border-rose-500/20 text-rose-300",
        emerald: "bg-emerald-500/10 border-emerald-500/20 text-emerald-300",
        amber: "bg-amber-500/10 border-amber-500/20 text-amber-300",
        purple: "bg-purple-500/10 border-purple-500/20 text-purple-300"
    };
    return (
        <div className={`inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border text-xs sm:text-sm font-semibold tracking-wide uppercase ${colorClasses[color]}`}>
            <IconComp className="w-4 h-4" /> {label}
        </div>
    );
};

const StatCard = ({ value, label, sub, accent = "indigo" }) => {
    const colors = {
        indigo: "from-indigo-900/40 to-slate-900/80 border-indigo-500/30",
        emerald: "from-emerald-900/40 to-slate-900/80 border-emerald-500/30",
        purple: "from-purple-900/40 to-slate-900/80 border-purple-500/30",
        amber: "from-amber-900/40 to-slate-900/80 border-amber-500/30",
        rose: "from-rose-900/40 to-slate-900/80 border-rose-500/30"
    };
    return (
        <div className={`bg-gradient-to-br ${colors[accent]} border rounded-2xl p-5 sm:p-6 text-center h-full flex flex-col justify-center`}>
            <div className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-1 whitespace-nowrap leading-none">{value}</div>
            <div className="text-xs sm:text-sm font-semibold text-white/80 mt-2">{label}</div>
            {sub && <div className="text-[10px] sm:text-xs text-white/50 mt-1.5">{sub}</div>}
        </div>
    );
};

const Check = ({ children, highlight = false }) => (
    <li className="flex gap-3 items-start">
        <CheckCircle2 className={`w-5 h-5 mt-0.5 flex-shrink-0 ${highlight ? 'text-indigo-300' : 'text-emerald-400'}`} />
        <span className={`leading-relaxed text-sm sm:text-base ${highlight ? 'text-indigo-100 font-medium' : 'text-slate-200'}`}>{children}</span>
    </li>
);

/* ───────────── Main Component ───────────── */
export default function AbirShowEngine() {

    useEffect(() => {
        document.title = `${SHOW_NAME} | Distribution Engine`;
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pb-20 pt-24 relative z-10">

            {/* Print overrides — gradient text shows as white in PDF */}
            <style>{`
                @media print {
                    [class*="bg-clip-text"] {
                        -webkit-text-fill-color: white !important;
                        color: white !important;
                        background: none !important;
                        background-image: none !important;
                    }
                }
            `}</style>

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">{HOST_NAME} · Distribution Engine</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Proposal</span>
                </div>
            </header>

            <main className="max-w-[1120px] mx-auto px-4 space-y-24 sm:space-y-32">

                {/* ───────── HERO ───────── */}
                <MotionSection className="text-center pt-6 sm:pt-10">
                    <SectionTag icon={Mic} label={`Distribution Engine · Prepared for ${HOST_NAME}`} />
                    <h1 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-tight pb-2">
                        From show.<br />To feed.
                    </h1>
                    <p className="text-base sm:text-xl md:text-2xl text-white/60 max-w-3xl mx-auto leading-relaxed mb-4">
                        A multi-page content engine for {SHOW_NAME}. We turn every episode into a network of clips, posted daily across every page and platform you own.
                    </p>
                    <p className="text-sm sm:text-lg md:text-xl text-white/45 max-w-3xl mx-auto leading-relaxed mb-12">
                        Same team that runs networks for Raj Shamani, Kush Desai and Badshah.
                    </p>

                    <div className="flex justify-center mb-12">
                        <a
                            href="#tiers"
                            className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-indigo-500 hover:bg-indigo-400 transition text-white font-bold text-sm shadow-lg shadow-indigo-500/30"
                        >
                            <Target className="w-4 h-4" /> See the two tiers
                        </a>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 max-w-4xl mx-auto gap-3 sm:gap-4">
                        <StatCard value="60" label="Unique clips" sub="cut from your show, monthly" accent="indigo" />
                        <StatCard value="×2" label="Pages" sub="main + themed fan page" accent="purple" />
                        <StatCard value="×3" label="Platforms" sub="Instagram · YouTube · Facebook" accent="amber" />
                        <StatCard value="360-480" label="Posts in feeds" sub="every month, all platforms" accent="emerald" />
                    </div>
                </MotionSection>

                {/* ───────── THE MULTIPLICATION ───────── */}
                <MotionSection>
                    <div className="text-center mb-10">
                        <SectionTag icon={Workflow} label="The math" color="purple" />
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4">One episode.<br />Multiplied across the network.</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            Every show recording becomes 60 clips, posted across two pages, on three platforms. The compounding is the whole point.
                        </p>
                    </div>

                    <div className="relative rounded-3xl border border-white/10 bg-gradient-to-b from-indigo-900/10 to-purple-900/5 backdrop-blur p-6 sm:p-12 overflow-hidden">
                        {/* Decorative glow */}
                        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none"></div>
                        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-purple-500/10 blur-3xl pointer-events-none"></div>

                        <div className="relative grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 mb-10">
                            {[
                                { icon: Mic, n: "1", l: "Episode", a: "indigo" },
                                { icon: Film, n: "60", l: "Unique clips", a: "purple" },
                                { icon: Layers, n: "× 2", l: "Pages", a: "amber" },
                                { icon: Share2, n: "× 3", l: "Platforms", a: "emerald" }
                            ].map((step, i) => {
                                const accentBg = {
                                    indigo: "bg-indigo-500/15 text-indigo-300",
                                    purple: "bg-purple-500/15 text-purple-300",
                                    amber: "bg-amber-500/15 text-amber-300",
                                    emerald: "bg-emerald-500/15 text-emerald-300"
                                }[step.a];
                                return (
                                    <div key={i} className="text-center flex flex-col items-center">
                                        <div className={`${accentBg} w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center mb-4`}>
                                            <step.icon className="w-6 h-6 sm:w-7 sm:h-7" />
                                        </div>
                                        <div className="text-3xl sm:text-4xl md:text-5xl font-black text-white tabular-nums leading-none mb-2">{step.n}</div>
                                        <div className="text-[10px] sm:text-xs font-bold tracking-widest uppercase text-white/50">{step.l}</div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="relative flex items-center justify-center gap-4 my-8">
                            <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent flex-1"></div>
                            <span className="text-white/30 text-xs font-bold tracking-widest uppercase">Equals</span>
                            <div className="h-px bg-gradient-to-r from-transparent via-white/20 to-transparent flex-1"></div>
                        </div>

                        <div className="relative text-center">
                            <div className="text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase text-indigo-300/80 mb-3">Total monthly output · Tier 1</div>
                            <div className="text-6xl sm:text-7xl md:text-8xl font-black bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 tracking-tight tabular-nums leading-none mb-3">
                                360 — 480
                            </div>
                            <div className="text-sm sm:text-base text-white/60">posts landing in audience feeds</div>
                        </div>
                    </div>
                </MotionSection>

                {/* ───────── WHAT'S IN THE BOX ───────── */}
                <MotionSection>
                    <div className="text-center mb-10">
                        <SectionTag icon={Layers} label="What's in the box" />
                        <h2 className="text-3xl sm:text-4xl font-black mb-4">A proven engine.<br />Applied to your show.</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            We don't invent the system for you. We point an existing, running engine at {SHOW_NAME}.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
                        <div className="rounded-2xl border border-indigo-500/30 bg-indigo-900/10 p-5 sm:p-6">
                            <div className="bg-indigo-500/15 w-11 h-11 rounded-xl flex items-center justify-center mb-4">
                                <Camera className="w-5 h-5 text-indigo-300" />
                            </div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-2 leading-snug">Editor bar</h3>
                            <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                                The same operators who cut for Raj Shamani, Kush Desai and Badshah work your show. No outsourced freelancers.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-purple-500/30 bg-purple-900/10 p-5 sm:p-6">
                            <div className="bg-purple-500/15 w-11 h-11 rounded-xl flex items-center justify-center mb-4">
                                <LayoutGrid className="w-5 h-5 text-purple-300" />
                            </div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-2 leading-snug">Multi-page network</h3>
                            <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                                Main page carries the brand. Fan pages compound reach surface. Algorithmically distinct accounts, one engine.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-amber-500/30 bg-amber-900/10 p-5 sm:p-6">
                            <div className="bg-amber-500/15 w-11 h-11 rounded-xl flex items-center justify-center mb-4">
                                <ImageIcon className="w-5 h-5 text-amber-300" />
                            </div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-2 leading-snug">Platform-native cuts</h3>
                            <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                                Each platform gets the cut it rewards. IG vertical with on-screen captions, YouTube with thumbnails, Facebook with native upload.
                            </p>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-900/10 p-5 sm:p-6">
                            <div className="bg-emerald-500/15 w-11 h-11 rounded-xl flex items-center justify-center mb-4">
                                <Workflow className="w-5 h-5 text-emerald-300" />
                            </div>
                            <h3 className="text-base sm:text-lg font-bold text-white mb-2 leading-snug">Daily publishing</h3>
                            <p className="text-slate-300 text-xs sm:text-sm leading-relaxed">
                                Cut, captioned, scheduled, published, measured. Every day, across every surface. No gaps.
                            </p>
                        </div>
                    </div>
                </MotionSection>

                {/* ───────── PRICING TIERS ───────── */}
                <MotionSection id="tiers">
                    <div className="text-center mb-12">
                        <SectionTag icon={Calendar} label="Two tiers" color="amber" />
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4">Pick your scale.<br />Same system.</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            Same engine. Same editor bar. Same publishing cadence. The only difference is how many pages we run and how many clips we produce.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-7 max-w-5xl mx-auto">

                        {/* TIER 1 — PILLAR */}
                        <div className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur p-7 sm:p-9 flex flex-col">
                            <div className="text-white/40 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-3">Tier 1 · Pillar</div>
                            <div className="text-xl sm:text-2xl font-black text-white mb-6 leading-snug">Your show, multiplied across one fan network.</div>

                            <div className="mb-7">
                                <div className="text-5xl sm:text-6xl font-black text-white tracking-tight tabular-nums leading-none">₹1,25,000</div>
                                <div className="text-xs sm:text-sm text-white/40 mt-2">per month · all inclusive</div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-7">
                                <StatCard value="60" label="Clips" sub="unique, monthly" accent="indigo" />
                                <StatCard value="2" label="Pages" sub="main + 1 fan" accent="purple" />
                                <StatCard value="3" label="Platforms" sub="IG / YT / FB" accent="amber" />
                                <StatCard value="360-480" label="Posts" sub="in feeds, monthly" accent="emerald" />
                            </div>

                            <div className="h-px bg-white/10 my-2"></div>

                            <ul className="space-y-3.5 mt-5 mb-7">
                                <Check>Daily publishing across 6 surfaces (2 pages × 3 platforms)</Check>
                                <Check>QC pass on every clip before it ships</Check>
                                <Check>Editor direction + caption writing in show voice</Check>
                                <Check>Weekly performance check-in</Check>
                            </ul>
                        </div>

                        {/* TIER 2 — NETWORK (RECOMMENDED) */}
                        <div className="relative rounded-3xl border-2 border-indigo-500/50 bg-gradient-to-b from-indigo-900/30 to-indigo-900/5 backdrop-blur p-7 sm:p-9 flex flex-col shadow-[0_0_60px_-15px_rgba(99,102,241,0.4)]">
                            <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-indigo-500 text-white text-[10px] sm:text-xs font-black tracking-widest uppercase px-4 py-1.5 rounded-full shadow-lg shadow-indigo-500/40">
                                Recommended
                            </div>

                            <div className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-3">Tier 2 · Network</div>
                            <div className="text-xl sm:text-2xl font-black text-white mb-6 leading-snug">Compounding reach across a full multi-page network.</div>

                            <div className="mb-7">
                                <div className="text-5xl sm:text-6xl font-black bg-clip-text text-transparent bg-gradient-to-r from-white to-indigo-300 tracking-tight tabular-nums leading-none">₹2,00,000</div>
                                <div className="text-xs sm:text-sm text-white/50 mt-2">per month · all inclusive</div>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-7">
                                <StatCard value="90" label="Clips" sub="unique, monthly" accent="indigo" />
                                <StatCard value="4" label="Pages" sub="main + 3 fan" accent="purple" />
                                <StatCard value="3" label="Platforms" sub="IG / YT / FB" accent="amber" />
                                <StatCard value="~1,080" label="Posts" sub="3× Tier 1 output" accent="emerald" />
                            </div>

                            <div className="h-px bg-indigo-500/20 my-2"></div>

                            <ul className="space-y-3.5 mt-5 mb-7">
                                <Check highlight>Everything in Tier 1</Check>
                                <Check highlight>3 themed fan pages instead of 1</Check>
                                <Check highlight>Custom YouTube thumbnails on every clip</Check>
                                <Check highlight>Monthly performance dashboard</Check>
                                <Check highlight>Priority editor allocation</Check>
                            </ul>
                        </div>
                    </div>
                </MotionSection>

                {/* ───────── WHAT WE MEASURE (no promises) ───────── */}
                <MotionSection>
                    <div className="text-center mb-10">
                        <SectionTag icon={BarChart3} label="Transparent reporting" color="emerald" />
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4">What we measure.<br />Every month.</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            We surface the numbers honestly, every month. No projected guarantees in this proposal. This is the first time we're applying the system to a show like yours.
                        </p>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 max-w-5xl mx-auto mb-8">
                        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/20 to-slate-900/60 p-5 sm:p-6">
                            <div className="bg-emerald-500/15 w-11 h-11 rounded-xl flex items-center justify-center mb-4">
                                <Eye className="w-5 h-5 text-emerald-300" />
                            </div>
                            <div className="text-base sm:text-lg font-bold text-white mb-1">Reach</div>
                            <div className="text-xs sm:text-sm text-white/55 leading-snug">Per page, per platform</div>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/20 to-slate-900/60 p-5 sm:p-6">
                            <div className="bg-emerald-500/15 w-11 h-11 rounded-xl flex items-center justify-center mb-4">
                                <TrendingUp className="w-5 h-5 text-emerald-300" />
                            </div>
                            <div className="text-base sm:text-lg font-bold text-white mb-1">Watch time</div>
                            <div className="text-xs sm:text-sm text-white/55 leading-snug">Average and median, by clip</div>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/20 to-slate-900/60 p-5 sm:p-6">
                            <div className="bg-emerald-500/15 w-11 h-11 rounded-xl flex items-center justify-center mb-4">
                                <UserPlus className="w-5 h-5 text-emerald-300" />
                            </div>
                            <div className="text-base sm:text-lg font-bold text-white mb-1">Follower growth</div>
                            <div className="text-xs sm:text-sm text-white/55 leading-snug">Net new per page</div>
                        </div>
                        <div className="rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/20 to-slate-900/60 p-5 sm:p-6">
                            <div className="bg-emerald-500/15 w-11 h-11 rounded-xl flex items-center justify-center mb-4">
                                <LineChart className="w-5 h-5 text-emerald-300" />
                            </div>
                            <div className="text-base sm:text-lg font-bold text-white mb-1">Platform lift</div>
                            <div className="text-xs sm:text-sm text-white/55 leading-snug">Where the system compounds</div>
                        </div>
                    </div>

                    <div className="max-w-2xl mx-auto rounded-2xl border border-amber-500/30 bg-amber-500/5 p-5 sm:p-6 flex items-start gap-4">
                        <div className="bg-amber-500/15 p-2.5 rounded-xl shrink-0">
                            <Trophy className="w-5 h-5 text-amber-300" />
                        </div>
                        <p className="text-amber-50/90 text-sm sm:text-base leading-relaxed">
                            We don't put a guaranteed reach number in this proposal. The honest answer to "how much reach" is — let's run it and measure.
                        </p>
                    </div>
                </MotionSection>

                {/* ───────── SOCIAL PROOF ───────── */}
                <MotionSection>
                    <div className="text-center mb-10">
                        <SectionTag icon={ShieldCheck} label="The team" color="purple" />
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black mb-4">Same operators.<br />Same bar.</h2>
                        <p className="text-slate-400 max-w-2xl mx-auto text-sm sm:text-base">
                            Not a separate team for new clients. The exact people who cut for the names below cut for {HOST_NAME}.
                        </p>
                    </div>

                    <div className="grid grid-cols-3 max-w-3xl mx-auto gap-3 sm:gap-4 mb-10">
                        <StatCard value="450M+" label="Views" sub="last 6 months" accent="purple" />
                        <StatCard value="100M+" label="Monthly views" sub="right now" accent="indigo" />
                        <StatCard value="4" label="Active networks" sub="being run" accent="amber" />
                    </div>

                    <div className="flex flex-wrap items-center justify-center gap-3 max-w-3xl mx-auto">
                        {["Raj Shamani", "Kush Desai", "Badshah", "BJP"].map((name) => (
                            <div key={name} className="rounded-full border border-white/15 bg-white/5 backdrop-blur-sm px-5 py-2.5 text-sm sm:text-base font-bold text-white/85">
                                {name}
                            </div>
                        ))}
                    </div>
                </MotionSection>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-10 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
